// buildConcepts.ts — Stage 0 of the build-surfaces simplification
// (docs/planning/BUILD-SURFACES-SIMPLIFICATION-SEP22.md §7: decisions taken
// 2026-09-22). ONE typed table of one-line definitions for every "thing you
// build" concept, so every UI surface that needs to explain a Build concept
// (the Definitions kind switcher, the Build-hub tab strip, the unified
// Create menu, the "What's the difference?" panel, and the tenant-visible
// glossary page) reads from this file and can never drift from it.
//
// This is copy/labels only — it changes NO routes, NO API shapes, and NO
// backing tables. Every `key` below maps 1:1 onto an EXISTING, unchanged
// concept (DefinitionKindKey for the four Definitions facets, or an
// existing Build-hub tab / deep link for the rest). Renaming a UI label is
// safe (see navigation.ts's P2.435 comments); this file follows the exact
// same "keep the old word as an alias, never touch the backing name" rule.
//
// Canonical decisions this table encodes (BUILD-SURFACES-SIMPLIFICATION-SEP22.md §7):
//  1. Canonical noun is "Workflow", with Simple and Graph canvas modes.
//     "Agent Graph" stays as a UI alias for the Graph mode (and for the
//     `stategraph` engine/API — that name is NOT renamed, see ADR-S19-1 /
//     P2.435 precedent).
//  2. Council becomes a Team mode ("Debate and vote") once merged — today
//     it still lives at Governance → AI Council, so the Team entry says so
//     and links there instead of overclaiming a merge that hasn't shipped.
//  3. Loop keeps its name. It has no Build-hub create surface yet (Stage 2
//     work) — its `createPath` deep-links to where it's actually created/
//     monitored today (Monitor → Loops), per the ontology consult's
//     "link out, don't invent a create flow" recommendation.
//  4. Chain and Runbook keep their existing data models/routes untouched.

export type BuildConceptKey =
  | 'workflow'
  | 'agent_graph'
  | 'chain'
  | 'runbook'
  | 'agent'
  | 'team'
  | 'loop'
  | 'template'
  | 'skill'
  | 'use_case'

/**
 * Execution placement fact (BUILD-SURFACES-SIMPLIFICATION-SEP22.md §8.3/§8.4
 * decision 3, ADR-191). 'platform' = runs in-process on the Vharta control
 * plane, no BYOK cluster involved (Agent Graph routing/LLM nodes, Team).
 * 'byok' = dispatches to the tenant's own BYOK cluster (Workflow, Chain —
 * each step is a Workflow execution). 'per-run' = the concept itself has no
 * fixed placement; it inherits whatever its target/child resolves to at run
 * time (Loop's target Workflow, Template's resulting Workflow, Skill's
 * Agent, Use case's deployed Workflows/Teams/Loops). See RunsOnLine.tsx
 * (components/shared/) — the one component that renders this fact as a
 * "Runs on:" line in every definition header and Run dialog.
 */
export type PlacementKind = 'platform' | 'byok' | 'per-run'

export interface BuildConcept {
  key: BuildConceptKey
  /** Canonical UI label — matches the tab/segmented-control label exactly. */
  label: string
  /** One sentence: what it is. Used in tooltips, so keep it short. */
  definition: string
  /** One sentence: when to reach for this instead of a neighboring concept. */
  whenToUse: string
  /**
   * Every name this concept has ever been called in the product or the
   * code/API/DB layer, kept searchable forever (P2.435 precedent — see
   * navigation.ts's HubTab.aliases doc comment). Purely descriptive here;
   * the actual CommandPalette search list still lives in navigation.ts.
   */
  aliases: string[]
  /**
   * Where a tenant actually creates one today. Every one of these routes
   * already exists — this table adds no new route. `null` means there is
   * no dedicated create surface; the concept is always created as a step
   * inside something else (kept for completeness / future rows only).
   */
  createPath: string | null
  /** Where this concept executes — see PlacementKind's doc comment. */
  placement: PlacementKind
  /** One-line placement clause, used by the unified Create menu alongside `definition`. */
  placementCopy: string
}

export const BUILD_CONCEPTS: BuildConcept[] = [
  {
    key: 'workflow',
    label: 'Workflow',
    // Deliberately does NOT say the agentic engine "displays as Workflow" —
    // that contradicts the live FE source of truth for engine labels,
    // web/src/features/engines/engineDisplay.ts (issue #99), whose
    // ENGINE_LABELS.agentic is "Agentic" (its own doc comment: "Bare
    // 'Workflow' is retired as a label entirely; nothing owns that word
    // now"). "Workflow" here names the Build-hub DEFINITION you author;
    // "Agentic" is the separate ENGINE BADGE shown on its runs in
    // Monitor/Cost/License — two different UI surfaces, allowed to use
    // different words for the same underlying engine. See
    // buildConcepts.test.ts's engineDisplay-consistency test.
    definition:
      'A definition you build that runs; today its canvas comes in a Simple mode (one agent) and a Graph mode (Agent Graph). Engine badges (Agentic, Langflow, Airflow) come from engineDisplay.ts.',
    whenToUse: 'Start here for anything that runs end-to-end and produces an output.',
    aliases: ['workflow', 'workflows', 'automation flow', 'flow'],
    createPath: '/tenant/build/workflows/new',
    placement: 'byok',
    placementCopy: 'Runs on a BYOK cluster you configure.',
  },
  {
    key: 'agent_graph',
    label: 'Agent Graph',
    definition:
      "Agent Graph is the Graph mode of a Workflow — a branching, multi-step canvas (LLM Call, Tool Call, Human Review, Condition Router, and more). The code, API, and database call this 'stategraph'.",
    whenToUse:
      "Pick Graph mode when a Simple (single-agent) Workflow isn't enough — you need branching, multiple steps, or a human-review gate.",
    aliases: ['agent graph', 'agent graphs', 'stategraph', 'state graph', 'graph'],
    createPath: '/tenant/build/agent-graphs/new',
    placement: 'platform',
    placementCopy: 'Runs on the Vharta platform, not a BYOK cluster.',
  },
  {
    key: 'chain',
    label: 'Chain',
    definition:
      'An ordered list of existing Workflows run in sequence, with conditional branching, parallel groups, and sub-chain nesting.',
    whenToUse: 'Pick Chain when you already have several Workflows and just need to run them in order.',
    aliases: ['chain', 'chains', 'pipeline'],
    createPath: '/tenant/build/workflows?kind=chain',
    placement: 'byok',
    placementCopy: 'Runs on a BYOK cluster you configure (same as Workflow — each step is a Workflow execution).',
  },
  {
    key: 'runbook',
    label: 'Runbook',
    definition:
      'An alert-triggered playbook of infrastructure actions (restart, drain, rollback, notify) with approval gates and circuit breakers.',
    whenToUse:
      'Pick Runbook for SRE / incident remediation — it never references a Workflow or an Agent, so it is not a competing way to do the same thing.',
    aliases: ['runbook', 'runbooks'],
    createPath: '/tenant/build/runbooks/new',
    placement: 'byok',
    placementCopy: 'Runs its actions against your BYOK infrastructure.',
  },
  {
    key: 'agent',
    label: 'Agent',
    definition: 'A reusable AI worker — model, prompt, tools, and skills — you can run standalone or embed in a Workflow or Team.',
    whenToUse: 'Pick Agent when you want a reusable persona you can drop into multiple Workflows or Teams.',
    aliases: ['agent', 'bot', 'ai agent'],
    createPath: '/tenant/build/agents',
    // Review round 2 (fix/runs-on-line-everywhere) — NOT a static 'platform'
    // fact. §8.4 decision 5 / ADR-191 Stage B makes an agent's dispatch
    // conditional on the RUN's resolved placement: today (Stage A, before
    // decision 5 ships) an agent reference always executes in-process on the
    // control plane (ADDExecutionRunner); once Stage B lands, a BYOK
    // tenant's agent reference dispatches through the chat pipeline to the
    // tenant's own cluster instead. The fact this table can state is "it
    // depends on the run", not a single kind — 'per-run', same bucket as
    // Loop/Template/Skill/Use case. The RENDERED "Runs on:" line for any
    // given run must always come from that run's resolved placement (e.g.
    // the `/plan` response's placement block, once it exists), never from
    // this static table — this table is Create-menu/glossary copy only.
    placement: 'per-run',
    placementCopy:
      'Runs on the Vharta platform today; once its placement resolves to BYOK, it runs on the tenant\'s own cluster instead.',
  },
  {
    key: 'team',
    label: 'Team',
    definition:
      "A roster of Agents that work together with a shared budget and topology. “Council = a Team that debates and votes” once merged — today, debate/vote panels are still configured at Governance → AI Council.",
    whenToUse: 'Pick Team when you need several Agents collaborating on one task, or (via AI Council today) a panel that debates before deciding.',
    aliases: ['team', 'teams', 'council', 'ai council'],
    createPath: '/tenant/build/teams',
    placement: 'platform',
    placementCopy: 'Runs on the Vharta platform, not a BYOK cluster.',
  },
  {
    key: 'loop',
    label: 'Loop',
    definition: 'A goal that re-checks itself on an interval, with memory across ticks — for polling and alerting, not one-off runs.',
    whenToUse: 'Pick Loop when you want something to keep watching and re-evaluating on a schedule, not just run once.',
    aliases: ['loop', 'loops', 'watcher'],
    // Loop has no Build-hub create surface yet (Stage 2 per the sequencing
    // plan) — deep-link to where it's actually created/monitored today
    // rather than inventing a new create flow (ontology consult
    // recommendation: "link out, don't invent a create flow").
    createPath: '/tenant/monitor?tab=loops',
    placement: 'per-run',
    placementCopy: "Its target Workflow's placement applies.",
  },
  {
    key: 'template',
    label: 'Template',
    definition: 'A reusable starting point for a single Workflow.',
    whenToUse: 'Pick Template to start a new Workflow from a proven starting point, not to package a multi-object bundle (see Use case).',
    aliases: ['template', 'templates'],
    createPath: '/tenant/build/templates',
    placement: 'per-run',
    placementCopy: 'Inherits the placement of the Workflow it starts.',
  },
  {
    key: 'skill',
    label: 'Skill',
    definition: 'A reusable capability (prompt + config) that an Agent can be given.',
    whenToUse: 'Pick Skill to package a capability you want to reuse across multiple Agents.',
    aliases: ['skill', 'skills'],
    createPath: '/tenant/build/skills',
    placement: 'per-run',
    placementCopy: 'Inherits the placement of the Agent it is given to.',
  },
  {
    key: 'use_case',
    label: 'Use case',
    definition: 'A packaged, deployable bundle of Workflows, Teams, Loops, and configuration for a specific business scenario.',
    whenToUse: 'Pick Use case when you want a guided, ready-to-deploy solution rather than building each piece from scratch.',
    aliases: ['use case', 'use cases', 'automation', 'automations', 'blueprint', 'blueprints'],
    createPath: '/tenant/ai/use-cases',
    placement: 'per-run',
    placementCopy: 'Inherits the placement of each Workflow, Team, and Loop it deploys.',
  },
]

const BUILD_CONCEPT_BY_KEY: ReadonlyMap<BuildConceptKey, BuildConcept> = new Map(
  BUILD_CONCEPTS.map((c) => [c.key, c]),
)

export function getBuildConcept(key: BuildConceptKey): BuildConcept | undefined {
  return BUILD_CONCEPT_BY_KEY.get(key)
}

/**
 * "Agent" is overloaded five ways across the product (see
 * docs/ontology/GLOSSARY.md's "Agent (five meanings)" entry, added in the
 * same Stage 0 change that added this file). Kept here — not invented
 * separately in the glossary feature — so the definitions never drift
 * from each other; the glossary page renders this list verbatim.
 */
export interface AgentSense {
  key: string
  label: string
  description: string
}

export const AGENT_SENSES: AgentSense[] = [
  {
    key: 'persona',
    label: 'Persona (your role)',
    description:
      'A signed-in human’s role-derived capability set (tenant owner, admin, operator, viewer, …) — governs what UI you see. Nothing to do with AI.',
  },
  {
    key: 'agent-builder',
    label: 'Agent (Build → Agents)',
    description:
      'The reusable AI worker entity described above: model, prompt, tools, skills. This is what "Agent" means everywhere in Build unless stated otherwise.',
  },
  {
    key: 'cluster-agent',
    label: 'BYOK / cluster agent',
    description:
      'The background process (“vharta-agent”) that runs inside a tenant’s own Kubernetes cluster and dispatches real executions. Ops/infra vocabulary — not shown to tenants as "Agent" today.',
  },
  {
    key: 'agentic-engine',
    label: 'The "agentic" engine',
    description:
      'The Vharta-native engine behind a Workflow definition’s Simple/Graph canvas modes. Its run-engine badge (Monitor, Cost, License — see engineDisplay.ts) reads "Agentic" — it is not an AI agent by itself.',
  },
  {
    key: 'agent-graph',
    label: 'Agent Graph',
    description:
      'The Graph mode of a Workflow (see above). Named for its "LLM Call" step type, not because it is "a graph of Agents".',
  },
]
